import { useRouter } from "@/_router";
import { useAuth } from "@/models/useAuth";
import { useEffect, useRef } from "react";
import { useAppStore } from "@/controller";
import { actions } from "@/controller";
import { useTranslation } from "react-i18next";
import BackgroundAmbience from "@/components/BackgroundAmbience";
import WebCheckoutEntry from "@/components/WebCheckoutEntry";
import { CheckIcon } from "@/components/Icons";
import { isNativeApp } from "@/platform";
import { useUISlice } from "@/models/useUISlice";
import { parseMarketingOnboardingContext } from "@/onboarding-interactions";
import { logBrowserCrash, logClientEvent } from "@/telemetry";
import { getStoreProofTranslationValues } from "@/product-proof";
import toast from "react-hot-toast";
import { safeAuthRedirect } from "@/auth-identity";
import { currentAcquisitionAttribution } from "@/controller";

export default function AuthPage() {
  const router = useRouter();
  const { t, i18n } = useTranslation();
  const hasStartedRef = useRef(false);
  const storeProof = getStoreProofTranslationValues(
    i18n.resolvedLanguage || i18n.language,
  );
  const urlParams = new URLSearchParams(window.location.search);
  const action = urlParams.get("action");
  const source = urlParams.get("source");
  const purchaseEmail =
    new URLSearchParams(window.location.hash.slice(1)).get("email")?.trim() ||
    undefined;
  const redirectUrl = urlParams.get("redirectUrl");
  const redirectTarget = safeAuthRedirect(redirectUrl);
  const marketingContext = parseMarketingOnboardingContext(
    window.location.search,
  );

  useEffect(() => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;
    // Cached website links from before /checkout existed still arrive here.
    // Render the checkout entry below and never open the account form.
    if (action === "checkout") return;

    if (isNativeApp()) {
      // Native apps use a device-backed guest session. Keep /auth as a
      // web-only account entry point, including after stale deep links.
      useUISlice.getState().setIsNewSession(true);
      router.replace("/home");
      return;
    }

    if (marketingContext.persona) {
      useAppStore.getState().setOnboardingPersona(marketingContext.persona);
    }
    if (marketingContext.intent) {
      useAppStore.getState().setOnboardingIntent(marketingContext.intent);
    }
    if (marketingContext.persona || marketingContext.intent) {
      logClientEvent("acquisition.contextApplied", {
        intent: marketingContext.intent,
        persona: marketingContext.persona,
        source: "marketing_site",
      });
    }

    void actions.user.remoteConfig();

    const agentTarget = source === "agent-connect" && redirectTarget ? new URL(redirectTarget, window.location.origin) : undefined;
    const candidate = agentTarget?.searchParams.get("connection") || "";
    const connectionId = agentTarget?.pathname === "/agent-connect" && /^[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(candidate) ? candidate : undefined;
    let cancelled = false;
    const returnAgentFailure = async (error: "access_denied" | "server_error") => {
      if (connectionId && redirectTarget) {
        try {
          const result = await actions.agent.cancelConnection(connectionId, error);
          if (!("error" in result)) {
            window.location.replace(result.value);
            return;
          }
        } catch { /* Offline or unknown grants cannot produce a trusted callback. */ }
        try {
          sessionStorage.setItem(`agent-connect:${connectionId}`, "failed");
          window.location.replace(redirectTarget);
          return;
        } catch { /* Keep the error visible if tab storage is unavailable. */ }
      }
      toast.error(t("useAuth.signInFailed"));
    };

    const startSignIn = async () => {
      let assistant: string | undefined;
      if (source === "agent-connect") {
        const description = connectionId ? await actions.agent.describeConnection(connectionId) : undefined;
        if (!description || "error" in description) {
          await returnAgentFailure("server_error");
          return;
        }
        assistant = description.value || t("agentSettings.connectedAssistant");
      }
      if (source === "chrome-extension" || source === "agent-connect") {
        document.documentElement.removeAttribute(
          "data-one-transcribe-chrome-auth-ready",
        );
        // Hosted extension auth is an explicit account-selection flow. Do not
        // silently hand the extension whichever website account happened to
        // be open before this tab arrived.
        await useAuth.prepareAccountSwitch();
      }

      useAuth.signIn({
        forceSignIn: true,
        ...(connectionId ? { onCancel: () => { cancelled = true; void returnAgentFailure("access_denied"); }, onError: () => { void returnAgentFailure("server_error"); } } : {}),
        ...(assistant !== undefined ? { modalProps: { title: t("agentSettings.title"), subtitle: t("agentSettings.authorization", { assistant }) } } : {}),
        ...(source === "chrome-extension" && purchaseEmail
          ? {
              modalProps: {
                title: t("useAuth.protectPurchaseTitle"),
                subtitle: t("useAuth.protectPurchaseOptionsSubtitle", {
                  email: purchaseEmail,
                }),
                defaultValue: purchaseEmail,
                buttonText: t("useAuth.protectPurchaseButton"),
                googleButtonText: t("useAuth.protectPurchaseGoogleButton"),
              },
            }
          : {}),
        callback: async () => {
          // A late Google popup must not authorize a cancelled handoff.
          if (cancelled) return;
          if (connectionId) {
            try {
              const result = await actions.agent.authorize(connectionId);
              if (!("error" in result) && result.value) {
                // Only the authenticated server chooses the registered OAuth
                // callback. Never redirect to a URL supplied in page params.
                window.location.replace(result.value);
                return;
              }
            } catch {
              // Never show success after an unconfirmed authorization.
            }
            await returnAgentFailure("server_error");
            return;
          }
          if (source === "chrome-extension") {
            // This hosted handoff is the trusted point where an extension user
            // proves their account. The server merges this marker atomically so
            // a stale browser profile cannot erase another connected client.
            try {
              await actions.user.update({
                connectedClients: ["chromeExtension"],
              });
            } catch {
              // Profile enrichment must never strand a completed sign-in.
            }
            // The content script may navigate this tab back into the extension
            // as soon as readiness is visible. Publish it only after the
            // profile update settles so that navigation cannot abort the call.
            document.documentElement.setAttribute(
              "data-one-transcribe-chrome-auth-ready",
              "true",
            );
          }

          const attribution = currentAcquisitionAttribution();
          if (Object.keys(attribution).length > 0) {
            try {
              await actions.user.captureAcquisition(attribution);
            } catch {
              // Attribution must never strand a completed sign-in.
            }
          }

          if (redirectTarget) {
            window.location.replace(redirectTarget);
          } else {
            router.push("/home");
          }
        },
      });
    };
    void startSignIn().catch((error) => {
      logBrowserCrash("auth.accountSwitchPreparation", error);
      if (source === "agent-connect") void returnAgentFailure("server_error");
      else toast.error(t("useAuth.signInFailed"));
    });
  }, [
    action,
    marketingContext.intent,
    marketingContext.persona,
    purchaseEmail,
    redirectTarget,
    router,
    source,
    t,
  ]);

  if (action === "checkout") return <WebCheckoutEntry />;

  // Branded backdrop behind the sign-in modal. This is the moment of maximum
  // hesitation in the funnel — a blank white page here reads as broken.
  return (
    <div className="app-page-background fixed inset-0 bg-[var(--bg-primary)] text-[var(--text-primary)] flex items-center justify-center overflow-hidden">
      <BackgroundAmbience />

      <div className="relative z-10 flex flex-col items-center text-center px-6 max-w-sm">
        <img
          src="/logo.png"
          alt="1Transcribe"
          width={64}
          height={64}
          className="w-16 h-16 rounded-2xl shadow-lg mb-5"
        />
        <h1 className="text-[22px] font-bold tracking-tight mb-2">
          {t("authPage.title", { defaultValue: "Sign in to continue" })}
        </h1>
        <p className="text-[14px] text-[var(--text-secondary)] leading-relaxed mb-6">
          {t("authPage.subtitle", {
            defaultValue:
              "Your transcripts are saved to your account — pick up right where you left off, on any device.",
          })}
        </p>

        <div className="space-y-2.5 mb-8">
          {[
            t("authPage.bullet1", {
              defaultValue: "Unlimited transcriptions with a subscription",
            }),
            t("authPage.bullet2", {
              defaultValue: "Files up to 10 hours and 5 GB",
            }),
            t("authPage.bullet3", {
              defaultValue: "Cancel anytime, no questions asked",
            }),
          ].map((item, i) => (
            <div key={i} className="flex items-center justify-center gap-2">
              <div className="w-4 h-4 rounded-full bg-[var(--success)]/20 flex items-center justify-center shrink-0">
                <CheckIcon size={9} className="text-[var(--success)]" />
              </div>
              <span className="text-[13px] text-[var(--text-secondary)]">
                {item}
              </span>
            </div>
          ))}
        </div>

        <p className="text-[11px] font-bold text-[var(--text-muted)] tracking-wide">
          {t("authPage.trustLine", {
            ...storeProof,
            defaultValue:
              "★★★★★ {{rating}} · {{reviewCount}}+ Google Play reviews",
          })}
        </p>
      </div>
    </div>
  );
}
